import type { TPlayer } from 'video.js'

/** Dedup state for concurrent 433 responses. */
export type CslErrorState = {
  cslErrorFired: boolean
  knownCsl?: number
}

type CslErrorPlayer = Pick<TPlayer, 'error'>

export function createCslErrorState(): CslErrorState {
  return { cslErrorFired: false }
}

export function resetCslErrorState(state: CslErrorState): void {
  state.cslErrorFired = false
  state.knownCsl = undefined
}

type CslHttpRequest = { url: string }
type CslHttpResponse = { statusCode?: number } | null | undefined

/** Handle 433 CSL; returns true for 433 (handled or deduped). Run before sourceUrl guards. */
export function handleCslHttpResponse(
  player: CslErrorPlayer,
  request: CslHttpRequest,
  response: CslHttpResponse,
  sourceUrl: string | undefined,
  state: CslErrorState
): boolean {
  if (response?.statusCode !== 433) return false
  if (state.cslErrorFired) return true

  state.cslErrorFired = true
  const csl = resolveCslFromUrls(request.url, sourceUrl)
  if (csl !== undefined) state.knownCsl = csl

  player.error({
    code: 433,
    metadata: {
      requestUrl: request.url,
      statusCode: 433,
      ...(csl !== undefined && { csl }),
    },
  } as unknown as Parameters<CslErrorPlayer['error']>[0])

  return true
}

/** Parse `csl=N` from the `hdnts` query param. */
export function parseCslFromUrl(url: string): number | undefined {
  try {
    const hdnts = new URL(url).searchParams.get('hdnts')
    if (!hdnts) return undefined
    const match = /,csl=(\d+)/.exec(hdnts)
    return match ? Number(match[1]) : undefined
  } catch {
    return undefined
  }
}

/** First URL with a parseable CSL (chunks often lack `hdnts`). */
export function resolveCslFromUrls(
  ...urls: (string | undefined | null)[]
): number | undefined {
  for (const url of urls) {
    if (!url) continue
    const csl = parseCslFromUrl(url)
    if (csl !== undefined) return csl
  }
  return undefined
}
